import React from "react";
import {
  FaPhone,
  FaEnvelope,
  FaGlobe,
  FaMapMarkerAlt,
  FaBuilding,
  FaTimes,
  FaMobileAlt,
  FaPhoneAlt,
  FaHeadset,
} from "react-icons/fa";
import PageModal from "../../Dashboard/Components/PageModal";
import noImage from "../../assets/nologo.gif";

function SupplierDetailsModal({ supplier, onClose }) {
  // Helper function to get contact icon based on type
  const getContactIcon = (type) => {
    switch (type) {
      case "mobile":
        return <FaMobileAlt className="text-[#363062] w-5 h-5" />;
      case "landline":
        return <FaPhoneAlt className="text-[#363062] w-5 h-5" />;
      case "hotline":
        return <FaHeadset className="text-[#363062] w-5 h-5" />;
      case "email":
        return <FaEnvelope className="text-[#363062] w-5 h-5" />;
      case "website":
        return <FaGlobe className="text-[#363062] w-5 h-5" />;
      default:
        return <FaPhone className="text-[#363062] w-5 h-5" />;
    }
  };

  return (
    <PageModal>
      <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full mx-4 my-8 p-6">
        <div className="flex justify-between items-start mb-6">
          <div className="flex flex-col items-center gap-6">
            <img
              src={supplier.logo || noImage}
              className="w-full h-24 object-contain rounded-lg border border-gray-100"
              alt={`${supplier.name} logo`}
            />
            <h2 className="text-lg font-semibold text-[#363062]">
              {supplier.name}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-[#363062] transition-colors"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#363062]/10 flex items-center justify-center">
                <FaBuilding className="text-[#363062] w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Service Type</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {supplier.services.map((service, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-[#8FCD00]/10 text-[#8FCD00] rounded-full text-xs font-medium"
                    >
                      {service}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Primary Contact Information */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#363062]/10 flex items-center justify-center">
                <FaPhone className="text-[#363062] w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Primary Phone</p>
                <a
                  href={`tel:${supplier.phone}`}
                  className="text-[#363062] hover:text-[#363062]/80 transition-colors"
                >
                  {supplier.phone}
                </a>
              </div>
            </div>

            {supplier.email && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#363062]/10 flex items-center justify-center">
                  <FaEnvelope className="text-[#363062] w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Primary Email</p>
                  <a
                    href={`mailto:${supplier.email}`}
                    className="text-[#363062] hover:text-[#363062]/80 transition-colors"
                  >
                    {supplier.email}
                  </a>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            {supplier.address && supplier.address !== "not indicated" && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#363062]/10 flex items-center justify-center">
                  <FaMapMarkerAlt className="text-[#363062] w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="text-[#363062]">{supplier.address}</p>
                </div>
              </div>
            )}

            {supplier.website && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#363062]/10 flex items-center justify-center">
                  <FaGlobe className="text-[#363062] w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Website</p>
                  <a
                    href={supplier.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#363062] hover:text-[#363062]/80 transition-colors"
                  >
                    {supplier.website}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Additional Contact Details Section */}
        {supplier.contacts && supplier.contacts.length > 0 && (
          <div className="mt-8">
            <h3 className="text-sm font-medium text-gray-500 mb-4">
              Additional Contact Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {supplier.contacts.map(
                (contact, index) =>
                  contact.type &&
                  contact.detail && (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="w-10 h-10 rounded-lg bg-[#363062]/10 flex items-center justify-center">
                        {getContactIcon(contact.type)}
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 capitalize">
                          {contact.type}
                        </p>
                        {contact.type === "email" ? (
                          <a
                            href={`mailto:${contact.detail}`}
                            className="text-[#363062] hover:text-[#363062]/80 transition-colors"
                          >
                            {contact.detail}
                          </a>
                        ) : contact.type === "website" ? (
                          <a
                            href={contact.detail}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#363062] hover:text-[#363062]/80 transition-colors"
                          >
                            {contact.detail}
                          </a>
                        ) : (
                          <a
                            href={
                              contact.type === "mobile" ||
                              contact.type === "landline" ||
                              contact.type === "hotline"
                                ? `tel:${contact.detail}`
                                : contact.detail
                            }
                            className="text-[#363062] hover:text-[#363062]/80 transition-colors"
                          >
                            {contact.detail}
                          </a>
                        )}
                      </div>
                    </div>
                  )
              )}
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href={`tel:${supplier.phone}`}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#363062] rounded-lg hover:bg-[#363062]/90 transition-colors"
          >
            <FaPhone className="w-4 h-4" />
            Call
          </a>
          {supplier.email && (
            <a
              href={`mailto:${supplier.email}`}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#363062] rounded-lg hover:bg-[#363062]/90 transition-colors"
            >
              <FaEnvelope className="w-4 h-4" />
              Email
            </a>
          )}
          {supplier.website && (
            <a
              href={supplier.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#363062] rounded-lg hover:bg-[#363062]/90 transition-colors"
            >
              <FaGlobe className="w-4 h-4" />
              Visit Website
            </a>
          )}
        </div>
      </div>
    </PageModal>
  );
}

export default SupplierDetailsModal;
